import { BoxV2 as Box, Button, Flex, TextInput } from 'portal-commons';
import { FC, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { faSquareMinus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useControllers } from './hooks';
import { BulkLiftSuspensionFormValues } from './types';
import Modal from '@/components/ModalV2';

const DefaultFormValues: BulkLiftSuspensionFormValues = {
  explanation: '',
};

interface BulkLiftSuspensionModalProps {
  open?: boolean;
  disabled?: boolean;
  campaignUids: string[];
  onClose?: () => void;
  onSubmit?: (explanation: string) => void;
}

const BulkLiftSuspensionModal: FC<BulkLiftSuspensionModalProps> = ({
  open,
  disabled,
  campaignUids,
  onClose,
  onSubmit,
}) => {
  const {
    control,
    formState: { errors },
    reset,
    handleSubmit,
  } = useForm<BulkLiftSuspensionFormValues>({
    defaultValues: DefaultFormValues,
  });
  const { explanation, setExplanation, validateExplanation, explanationError } =
    useControllers(control);

  const handleCancel = () => {
    onClose?.();
  };

  const handleValid = (value: BulkLiftSuspensionFormValues) => {
    onSubmit?.(value.explanation);
  };

  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open]);

  return (
    <Modal
      data-testid="bulkLiftSuspensionModal"
      open={open}
      sx={{ padding: '24px 32px' }}
    >
      <form
        style={{ width: '100%' }}
        noValidate
        onSubmit={handleSubmit(handleValid)}
      >
        <Flex
          sx={{
            flexDirection: 'column',
            alignItems: 'center',
            gap: 'xl',
            color: 't.black70',
            fontWeight: 400,
            lineHeight: 'normal',
          }}
        >
          <Flex
            sx={{
              alignItems: 'center',
              gap: 's',
            }}
          >
            <Flex
              sx={{
                alignItems: 'center',
                justifyContent: 'center',
                width: '24px',
                height: '24px',
                color: 't.black80',
                fontSize: '22px',
              }}
            >
              <FontAwesomeIcon icon={faSquareMinus} />
            </Flex>
            <Box sx={{ color: 't.black90', fontWeight: 600, fontSize: 'H600' }}>
              Lift Suspension
            </Box>
          </Flex>

          <Box sx={{ fontSize: 'H300' }}>
            {campaignUids.length} campaign(s) selected for lift suspension.
          </Box>

          <Flex sx={{ flexDirection: 'column', gap: 'm', width: '100%' }}>
            <Flex
              sx={{
                flexDirection: 'column',
                gap: 'xs',
                fontWeight: 600,
                fontSize: 'H400',
              }}
            >
              <Box sx={{ color: 't.black90' }}>Campaign ID(s)</Box>
              <Box>{campaignUids.join(', ')}</Box>
            </Flex>

            <Box sx={{ fontSize: 'H100' }}>* Indicates a Required Field</Box>
            <TextInput
              data-testid="bulkLiftSuspensionModalExplanation"
              required
              disabled={disabled}
              error={!!explanationError?.message}
              label="Explanation"
              note={explanationError?.message}
              value={explanation}
              onChange={setExplanation}
              onBlur={validateExplanation}
            />
          </Flex>

          <Flex sx={{ gap: '28px' }}>
            <Button
              data-testid="bulkLiftSuspensionModalCancelButton"
              type="button"
              variant="outline"
              disabled={disabled}
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              data-testid="bulkLiftSuspensionModalSubmitButton"
              type="submit"
              disabled={
                disabled ||
                Object.values(errors).some((value) => !!value.message)
              }
            >
              Lift Suspension
            </Button>
          </Flex>
        </Flex>
      </form>
    </Modal>
  );
};

export default BulkLiftSuspensionModal;
